import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import '@fontsource/geist-mono/500.css';
import './SessionExpiredModal.css';

const DEFAULT_SECONDS = 15;
const ACCENT = '#4f4fd0';
const REDIRECT_DELAY_MS = 700;

function hex2rgba(hex, a) {
  const h = String(hex || ACCENT).replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return `rgba(79, 79, 208, ${a})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

function formatLastActivity(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} · ${hh}:${mi}`;
}

export default function SessionExpiredModal({
  open,
  onClose,
  onLoginAgain,
  autoRedirect = true,
  seconds = DEFAULT_SECONDS,
}) {
  const total = Math.min(60, Math.max(5, Number(seconds) || DEFAULT_SECONDS));
  const [left, setLeft] = useState(total);
  const [loading, setLoading] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [openedAt, setOpenedAt] = useState(() => new Date());
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    redirectedRef.current = false;
    setLeft(total);
    setLoading(false);
    setCancelled(false);
    setOpenedAt(new Date());
    return undefined;
  }, [open, total]);

  useEffect(() => {
    if (!open || !autoRedirect || cancelled || loading) return undefined;
    const timer = window.setInterval(() => {
      setLeft((prev) => {
        if (prev <= 1) {
          setLoading(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [open, autoRedirect, cancelled, loading]);

  useEffect(() => {
    if (!open || !loading || redirectedRef.current) return undefined;
    const t = window.setTimeout(() => {
      if (redirectedRef.current) return;
      redirectedRef.current = true;
      if (typeof onLoginAgain === 'function') onLoginAgain();
    }, REDIRECT_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [open, loading, onLoginAgain]);

  if (!open) return null;

  const accent = ACCENT;
  const accentSoft = hex2rgba(accent, 0.1);
  const showCountdown = autoRedirect && !cancelled;
  const countdownLabel = loading
    ? 'Abrindo a tela de login…'
    : `Redirecionando para o login em ${left}s`;
  const barWidth = `${loading ? 0 : Math.round((left / total) * 100)}%`;

  const handleLogin = () => {
    if (loading) return;
    setLoading(true);
  };

  const handleCancel = () => {
    setCancelled(true);
  };

  const handleClose = () => {
    // Como no modelo: X só cancela o auto-redirect; login fica no botão / countdown.
    setCancelled(true);
    setLoading(false);
    setLeft(total);
    if (!autoRedirect && typeof onClose === 'function') onClose();
  };

  const node = (
    <div
      className="session-expired-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
    >
      <div className="session-expired-modal">
        <button
          type="button"
          className="session-expired-close"
          onClick={handleClose}
          aria-label="Fechar"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className="session-expired-body">
          <div className="session-expired-icon" aria-hidden>
            <div className="session-expired-ring" style={{ borderColor: accent }} />
            <div className="session-expired-ring session-expired-ring--delay" style={{ borderColor: accent }} />
            <div className="session-expired-icon-core" style={{ background: accentSoft, color: accent }}>
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <g className="session-expired-plug-l">
                  <path d="M1.5 12h3" />
                  <rect x="4.5" y="8" width="5" height="8" rx="1.6" />
                  <path d="M9.5 10h2M9.5 14h2" />
                </g>
                <g className="session-expired-plug-r">
                  <rect x="13" y="8" width="5" height="8" rx="1.6" />
                  <path d="M18 12h4.5" />
                </g>
                <g className="session-expired-spark" stroke="#e8793a" strokeWidth="1.5">
                  <path d="M12.2 5.2V3.4" />
                  <path d="M9.9 5.9L8.9 4.6" />
                  <path d="M14.5 5.9l1-1.3" />
                  <path d="M12.2 18.8v1.8" />
                </g>
              </svg>
            </div>
          </div>

          <div className="session-expired-badge">
            <span className="session-expired-badge-dot" />
            Sessão encerrada
          </div>

          <div className="session-expired-copy">
            <h2 id="session-expired-title" className="session-expired-title">
              Você foi desconectado
            </h2>
            <p className="session-expired-text">
              Sua sessão expirou por inatividade ou o token de acesso não é mais válido. Faça login novamente para continuar de onde parou.
            </p>
          </div>

          <div className="session-expired-details">
            <div className="session-expired-detail-row">
              <span className="session-expired-detail-label">Motivo</span>
              <span className="session-expired-detail-value">Token de acesso expirado</span>
            </div>
            <div className="session-expired-detail-row">
              <span className="session-expired-detail-label">Última atividade</span>
              <span className="session-expired-detail-value session-expired-detail-value--mono">
                {formatLastActivity(openedAt)}
              </span>
            </div>
          </div>
        </div>

        <div className="session-expired-actions">
          <button
            type="button"
            className="session-expired-primary"
            style={{ background: accent }}
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="session-expired-spin" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                  <path d="M21 12a9 9 0 1 1-6.2-8.56" />
                </svg>
                <span>Redirecionando…</span>
              </>
            ) : (
              <>
                <span>Fazer login novamente</span>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </>
            )}
          </button>
        </div>

        {showCountdown ? (
          <div className="session-expired-countdown">
            <div className="session-expired-countdown-row">
              <span>{countdownLabel}</span>
              {!loading ? (
                <button type="button" className="session-expired-cancel" onClick={handleCancel}>
                  Cancelar
                </button>
              ) : null}
            </div>
            <div className="session-expired-progress">
              <div
                className="session-expired-progress-bar"
                style={{ background: accent, width: barWidth }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? ReactDOM.createPortal(node, document.body) : null;
}
